import { beneficiaryService } from "../../services/beneficiary.service";
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	BeneficiaryPayload,
	ConsentPayload,
	Phone,
} from "../../domain/beneficiary.interface";
import { INCENTIVE_HISTORY_QUERY_KEY } from "./queryKeys";

const {
	findAllV2,
	findByIdV2,
	create,
	update,
	updateReconfirm,
	getConsentForms,
	patchConsentForm,
	getConsentFormUrl,
	findProgramsV2,
	findProvidersV2,
	findProviderSites,
	disenroll,
	reenroll,
	findByName,
	getCurrentWeekUdtStatus,
	findListVisits,
	getEnrollmentPeriods,
	findIncentiveHistory,
	putBeneficiaryInvite,
	updatePayerPlan,
} = beneficiaryService;

export const useGetBeneficiaries = ({
	providerSite = [],
	programId = "",
	providerId = "",
	userId = "",
	status = ["ACTIVE"],
	search = "",
	pageNumber,
	pageSize,
}: {
	providerSite?: string[];
	programId?: string;
	providerId?: string;
	userId?: string;
	status?: string[];
	search?: string;
	pageNumber: number;
	pageSize: number;
}) => {
	const beneficiaries = useQuery({
		queryKey: ["sims-beneficiaries"],
		queryFn: () =>
			findAllV2({
				providerSite,
				programId,
				providerId,
				userId,
				status,
				search,
				pageNumber,
				pageSize,
			}),
		staleTime: 1000 * 60 * 60,
		enabled: false,
	});

	return beneficiaries;
};

/**
 * The cache key for a single beneficiary, and the prefix that matches every
 * entry for one.
 *
 * 🔴 SIMS-9064: these exist because the key is NOT just the beneficiary id.
 * Several places read and write this cache directly, and they used to hard-code
 * `["sims-beneficiary", beneficiaryId]`. When the enrollment joined the key
 * those hard-coded arrays silently stopped matching — `getQueryData` and
 * `setQueryData` match EXACTLY, unlike `invalidateQueries`, which matches by
 * prefix and kept working. A `as BeneficiaryResponse` cast on the read hid the
 * resulting `undefined` from the compiler and blanked the beneficiary page.
 *
 * So: build the exact key with `beneficiaryQueryKey`, and match every
 * enrollment's entry for a beneficiary with `beneficiaryQueryKeyPrefix`.
 * Direct cache access should use the PREFIX — a caller that knows the
 * beneficiary usually does not know which enrollment dimension is cached, and
 * guessing reintroduces exactly this bug.
 */
export const beneficiaryQueryKey = (
	beneficiaryId: string,
	enrollmentId?: string,
): (string | undefined)[] => [
	"sims-beneficiary",
	beneficiaryId,
	enrollmentId ?? "",
];

export const beneficiaryQueryKeyPrefix = (beneficiaryId: string): string[] => [
	"sims-beneficiary",
	beneficiaryId,
];

export const useGetBeneficiaryById = (
	beneficiaryId: string,
	enrollmentId?: string,
) => {
	return useQuery({
		// 🔴 SIMS-9064: `enrollmentId` MUST be part of the key.
		//
		// It changes what the request returns (`findByIdV2` appends it as a query
		// param), so leaving it out made the cache answer for the wrong argument:
		// once an entry existed for a participant, changing the selected
		// enrollment could not refetch, because as far as React Query was
		// concerned nothing had changed. With `staleTime` at an hour, a single
		// bad entry then persisted for the rest of the session.
		//
		// That is what broke the Activity Sidebar links: arriving on a new
		// participant while the previous participant's enrollment id was still in
		// state fetched a mismatched pair, and the correction that followed could
		// not dislodge it. A hard refresh worked because it started from an empty
		// cache. Empty string rather than `undefined` keeps the key stable across
		// the "no enrollment chosen yet" state instead of producing two keys for
		// the same request.
		queryKey: beneficiaryQueryKey(beneficiaryId, enrollmentId),
		queryFn: () => findByIdV2(beneficiaryId, enrollmentId),
		staleTime: 1000 * 60 * 60,
		enabled: !!beneficiaryId,
		retry: false,
	});
};

export const useCreateBeneficiary = () => {
	return useMutation({
		mutationFn: (data: BeneficiaryPayload) => create(data),
	});
};

export const useUpdateBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		mutationFn: (data: Partial<BeneficiaryPayload>) =>
			update(data, beneficiaryId, enrollmentId),
	});
};

/**
 * 🔴 SIMS-9325: reconfirming must refresh EVERY cached copy of the participant.
 *
 * The participant is cached once per enrollment dimension (see
 * `beneficiaryQueryKey`), and the profile always holds at least two — the `""`
 * entry created on mount and the selected enrollment's. Callers used to
 * `refetch()` only their own exact key, so the other copy kept
 * `isEligibility: true` for the hour-long `staleTime`, and "Enter result" kept
 * asking for a reconfirmation that had already happened until an F5.
 *
 * Invalidated by PREFIX, and only on success: a failed reconfirmation must leave
 * the participant still due for one.
 */
export const useUpdateReconfirmBeneficiary = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			userId: string;
			beneficiaryId: string;
			enrollmentId: string;
		}) => updateReconfirm(data.userId, data.beneficiaryId, data.enrollmentId),
		onSuccess: (_result, { beneficiaryId }) =>
			invalidateReconfirmedBeneficiary(queryClient, beneficiaryId),
	});
};

/**
 * What an eligibility reconfirmation changes on the client, shared by both
 * reconfirm endpoints so the two cannot drift apart again. Returned so that
 * `mutateAsync` resolves only once the refreshed participant is in the cache.
 */
export const invalidateReconfirmedBeneficiary = (
	queryClient: QueryClient,
	beneficiaryId: string,
): Promise<unknown> =>
	Promise.all([
		queryClient.invalidateQueries({
			queryKey: beneficiaryQueryKeyPrefix(beneficiaryId),
		}),
		queryClient.invalidateQueries({
			queryKey: ["beneficiary-enrollments", beneficiaryId],
		}),
	]);

export const useGetConsentForms = (beneficiaryId: string) => {
	return useQuery({
		queryKey: ["sims-beneficiaries-consent-forms"],
		queryFn: () => getConsentForms(beneficiaryId),
	});
};

export const usePatchConsentForm = (
	beneficiaryId: string,
	enrollmentId: string,
) => {
	return useMutation({
		mutationFn: (data: ConsentPayload) =>
			patchConsentForm(data, beneficiaryId, enrollmentId),
	});
};

/**
 * SIMS-6740: takes the ENROLLMENT, not the consent's `s3Url` — backend-2 resolves the object
 * itself rather than presigning a client-supplied URL.
 *
 * The query key now includes the enrollment id. Under the old static key every enrollment's
 * download shared one cache entry, so opening a second participant's consent could serve the
 * first one's URL from cache.
 */
export const useGetConsentFormUrl = (
	beneficiaryId: string,
	enrollmentId: string,
) => {
	return useQuery({
		queryKey: [
			"sims-beneficiaries-consent-form-url",
			beneficiaryId,
			enrollmentId,
		],
		queryFn: () => getConsentFormUrl(beneficiaryId, enrollmentId),
		enabled: !!beneficiaryId && !!enrollmentId,
	});
};

export const useGetProgramsByIds = (programIds: string[]) => {
	return useQuery({
		// SIMS-7598: key MUST include programIds. With a static key, callers that
		// pass different id-sets shared one 7-day-cached entry, so whichever fired
		// first won — and an empty array serializes to `?ids=` (backend returns
		// ALL programs), which then leaked into every consumer (e.g. the B2D
		// program dropdown showing all programs regardless of access). `enabled`
		// stops the empty-ids request entirely. Mirrors useGetParticipantPrograms.
		queryKey: ["sims-beneficiaries-programs", programIds],
		queryFn: () => findProgramsV2(programIds),
		staleTime: 1000 * 60 * 60 * 24 * 7,
		enabled: programIds.length > 0,
	});
};

export const useGetProviderSitesByOUId = ({
	ouIds,
	queryKey,
}: {
	ouIds: string[];
	queryKey?: string;
}) => {
	return useQuery({
		queryKey: [queryKey || "sims-beneficiaries-provider-sites", ouIds],
		queryFn: () => findProviderSites(ouIds),
		staleTime: 5 * 1000,
		enabled: ouIds.length > 0,
	});
};

export const useGetProvidersByProgramId = (
	programId: string[],
	providerSiteId: string[],
	includeAll?: boolean,
) => {
	return useQuery({
		queryKey: ["sims-beneficiaries-providers"],
		queryFn: () =>
			findProvidersV2(programId, providerSiteId, includeAll || false),
		staleTime: 1000 * 60 * 60 * 24 * 7,
		enabled:
			programId.filter(Boolean).length > 0 &&
			providerSiteId.filter(Boolean).length > 0,
	});
};

export const useDisenrollBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		mutationFn: (data: { reasons: string; notes: string; userId: string }) =>
			disenroll(data, beneficiaryId, enrollmentId),
	});
};

export const useReenrollBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		// SIMS-8923: the declared type was `{ userId: string }` while the only caller
		// (RegisterBeneficiary) passes the whole re-enrolment payload, so every other field
		// — the consent among them — travelled through here untyped. `reenroll` itself takes
		// Partial<BeneficiaryPayload>, which is what this now says.
		mutationFn: (data: Partial<BeneficiaryPayload>) =>
			reenroll(data, beneficiaryId, enrollmentId),
	});
};

export const useGetSearchBeneficiary = (searchName: string, userId: string) => {
	return useQuery({
		queryKey: ["get-beneficiary-searched"],
		queryFn: () => findByName(searchName, userId),
	});
};

export const useGetCurrentWeekUdtStatus = (
	enrollmentId: string,
	behaviorId?: string,
	treatmentConfigName?: string,
) => {
	return useQuery({
		queryKey: [
			"get-current-week-udt-status",
			enrollmentId,
			behaviorId,
			treatmentConfigName,
		],
		queryFn: () =>
			getCurrentWeekUdtStatus(enrollmentId, behaviorId, treatmentConfigName),
		enabled: !!enrollmentId,
		retry: false,
		// Disable caching to ensure fresh data for each activity
		gcTime: 0,
		staleTime: 0,
	});
};

export const useGetListVisits = (enrollmentId: string, rangeWeeks: string) => {
	return useQuery({
		queryKey: ["get-list-visits", enrollmentId, rangeWeeks],
		queryFn: () =>
			findListVisits(enrollmentId, rangeWeeks).then((data) =>
				data?.sort((a, b) => Number(b.visit) - Number(a.visit)),
			),
		enabled: !!enrollmentId,
		retry: false,
	});
};

export const usePatchParticipant = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (data: { email?: string; phone?: Phone[]; userId: string }) =>
			beneficiaryService.patchBeneficiary(beneficiaryId, data),
	});
};

export const useGetBeneficiaryEnrollmentPeriods = (beneficiaryId: string) => {
	return useQuery({
		queryKey: ["get-beneficiary-enrollment-periods", beneficiaryId],
		queryFn: () => getEnrollmentPeriods(beneficiaryId),
	});
};

export const useGetListIncentives = ({
	enrollmentId,
	pageNumber,
	pageSize,
}: {
	enrollmentId: string;
	pageNumber: number;
	pageSize: number;
}) => {
	const query = useQuery({
		queryKey: [INCENTIVE_HISTORY_QUERY_KEY, enrollmentId, pageNumber, pageSize],
		queryFn: () => findIncentiveHistory(enrollmentId, pageNumber, pageSize),
		enabled: !!enrollmentId,
		retry: false,
	});

	return {
		incentives: query.data,
		isLoading: query.isLoading,
		refetchIncentives: query.refetch,
	};
};

export const usePutBeneficiaryInvite = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (invitedBy: string) =>
			putBeneficiaryInvite(beneficiaryId, invitedBy),
	});
};

export const useUpdateBeneficiaryPayerPlan = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (data: {
			payerId: string;
			oldMemberIdNumber: string;
			newMemberIdNumber: string;
			changedBy?: string;
		}) => updatePayerPlan(beneficiaryId, data),
	});
};

export * from "./useGetBeneficiaryPayerPlans";

/**
 * SIMS-9006 — redeem across ALL of a participant's enrollments.
 *
 * The banked balance is the PARTICIPANT's money, not any one enrollment's. The
 * per-enrollment `useBankRedeem` could only ever spend the selected enrollment,
 * so anything earned on another one was unreachable. This posts to the
 * beneficiary-level route, which pools the balances and consumes them
 * oldest-first server-side, in one transaction.
 */
export const useCrossEnrollmentBankRedeem = ({
	beneficiaryId,
}: {
	beneficiaryId: string;
}) => {
	return useMutation({
		mutationFn: (data: {
			providerId: string;
			delivery: "EMAIL" | "LINK" | "PHONE";
			productId: string;
			productName: string;
			amount?: number;
		}) => beneficiaryService.postCrossEnrollmentBankRedeem(beneficiaryId, data),
	});
};

export { INCENTIVE_HISTORY_QUERY_KEY };
